import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpParams, HttpEvent } from '@angular/common/http';

import { catchError, map } from 'rxjs/operators';

import {
  checkValidResponse,
  logError,
  ServiceError,
  SignedUrlMetaData,
  AttachmentToUpload,
  AttachmentMetaData,
  ReconsiderationType,
  Domain,
} from '@cigna/chcp/shared/util';

import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class AttachmentService {
  public attachmentApi: string;

  public CONSUMER_CODE = '1000';

  constructor(
    private http: HttpClient,
    @Inject('secureApi') secureApi: string,
  ) {
    this.attachmentApi = `${secureApi}/attachments/v1`;
  }

  getAttachmentsSignedUrlToPutFile(
    adjustmentId: string,
    files: File[],
    domain: string,
  ): Observable<SignedUrlMetaData[] | ServiceError> {
    const body = {
      domain: domain.toUpperCase(),
      domainId: adjustmentId,
      files: files.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      })),
    };

    const params: HttpParams = new HttpParams().set(
      'domain',
      domain.toUpperCase(),
    );

    return this.http
      .post(`${this.attachmentApi}/signed-url-upload`, body, { params })
      .pipe(
        map(checkValidResponse),
        map(
          (res: { attachmentResponse: { signedUrls: SignedUrlMetaData[] } }) =>
            res.attachmentResponse.signedUrls,
        ),
        catchError(logError),
      );
  }

  putFileToS3(
    attachment: AttachmentToUpload,
  ): Observable<HttpEvent<unknown> | ServiceError> {
    const options = {
      headers: { 'x-amz-tagging': `attachment_id=${attachment.attachmentId}` },
      reportProgress: true,
      observe: 'events' as const,
    };

    return this.http
      .put(attachment.signedUrl, attachment.file, options)
      .pipe(catchError(logError));
  }

  getAttachmentsDetails(
    domain?: ReconsiderationType | Domain,
    domainId?: string,
    attachmentIds?: string[],
    reconStatus?: string,
    claimNumber?: string,
    tin?: string,
  ): Observable<AttachmentMetaData[] | ServiceError> {
    let params: HttpParams = new HttpParams().set(
      'isDraft',
      (reconStatus === 'DRAFT').toString(),
    );
    if (domain && domainId) {
      params = params
        .append('domain', domain.toUpperCase())
        .append('domainId', domainId);
    }

    if (attachmentIds?.length) {
      params = params.append('signedUrl', 'true');
      attachmentIds.forEach((id, i) => {
        params = params.append(`attachmentIds[${i}]`, id);
      });
    }

    if (claimNumber) {
      params = params
        .append('claimNumber', claimNumber)
        .append('pages', 'true');
    }

    if (tin) {
      params = params.append('tin', tin);
    }

    return this.http.get(this.attachmentApi, { params }).pipe(
      map(checkValidResponse),
      map(
        (res: { attachmentDetails: AttachmentMetaData[] }) =>
          res.attachmentDetails,
      ),
      catchError(logError),
    );
  }

  deleteAttachment(id: string) {
    const params: HttpParams = new HttpParams().set(
      'consumerCode',
      this.CONSUMER_CODE,
    );

    return this.http
      .delete(`${this.attachmentApi}/${id}`, {
        params,
      })
      .pipe(map(checkValidResponse), catchError(logError));
  }
}
